import {
  useFocusEffect,
  useIsFocused,
  useNavigation,
} from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { Divider } from 'react-native-elements';

import { PressableNeolexon } from '@/components/PressableNeolexon';
import { NoPatient } from '@/components/PatientList/NoPatient';
import { BodyWrapper } from '@/layouts/BodyWrapper';
import { Button } from '@/components/ui/therapist/Button';
import { Card } from '@/components/ui/therapist/Card';
import { DEFAULT_PROFILE_COLOR } from '@/components/ColorPicker';
import { Icon } from '@/components/Icon';
import { Modal } from '@/components/ui/therapist/Modal';
import { Tag } from '@/components/ui/therapist/Tag';
import { Text } from '@/components/ui/therapist/Text';
import { useResponsiveHeight } from '@/hooks/useResponsiveDimensions';
import colors, { appTheme } from '@/config/colors';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useOnHover } from '@/hooks/useOnHover';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import store, { useStoreActions, useStoreState } from '@/stores/store';
import { getInitials } from '@/utils/stringHelpers';
import { logError } from '@/utils/logHandling';
import { replacedPatientProfileHeader } from '@/utils/stringHelpers';

import { styles } from './styles';
import { showWarningCheck } from '@/utils/licenseCheck';
import {
  getAllPatientsLicenseStatus,
  PatientLicenseStatus,
} from '@/api/services/patient';
import { Patient } from '@/stores/store.types';
import { mergeArraysById } from '@/utils/arrays';
import { PatientFilter } from './PatientFilter';
import { usePatientFilter } from './usePatientFilter';

export function PatientList() {
  const listHeight = useResponsiveHeight(64);

  const [isDemoTherapistVisible, setIsDemoTherapistVisible] = useState(false);
  const [showFilter, setShowFilter] = useState<boolean>(false);

  const [enhancedPatients, setEnhancedPatients] = useState<
    (Patient & {
      license: PatientLicenseStatus | null;
    })[]
  >([]);

  const { patients, changeID, changeSelectedPatient, fetchPatients } =
    useDashboardContext();
  const { isHovered, handleHoverIn, handleHoverOut } = useOnHover();
  const { setShowLoadingSpinner } = store.getActions();

  const { isTherapistDemo } = useStoreState((store) => store);
  const isFocused = useIsFocused();
  // Read focus via a ref so the effect can guard on it without listing it as a
  // dependency — otherwise a focus flip would re-run the fetch with stale patients.
  const isFocusedRef = useRef(isFocused);
  isFocusedRef.current = isFocused;
  const { navigate } =
    useNavigation<StackNavigationProp<TherapistStackParamList>>();
  const { setGlobalPopupState } = useStoreActions((actions) => ({
    setGlobalPopupState: actions.setGlobalPopupState,
  }));

  const { filteredPatients, countFilterApplied, cancelFilter, filterProps } =
    usePatientFilter(enhancedPatients);

  const hasNoFilterResults =
    countFilterApplied > 0 && filteredPatients.length === 0;

  const { resetFilter } = filterProps;

  const handleAddPatient = useCallback(async () => {
    if (isTherapistDemo) {
      setIsDemoTherapistVisible(true);
      return;
    } else {
      try {
        const hasInternet = await hasInternetAsync();
        if (hasInternet) {
          navigate('AddPatientScreen');
        } else {
          setGlobalPopupState({
            headerText: 'Keine Verbindung zum neolexon Server',
            bodyText:
              'Überprüfen Sie Ihre Internetverbindung und versuchen es erneut.',
            leftButton: '',
            rightButton: '',
          });
        }
      } catch (error) {
        logError('handleAddPatient in SideBar', error);
      }
    }
  }, [hasInternetAsync, navigate]);

  const fetchLicenseStatus = useCallback(
    async (patientsList?: Patient[] | null) => {
      try {
        if (!patientsList) {
          return;
        }

        const licenseStatus = await getAllPatientsLicenseStatus();
        const licenseStatusForPatients = mergeArraysById(
          patientsList,
          licenseStatus ?? [],
          '_id',
          'patientId',
        );
        setEnhancedPatients(licenseStatusForPatients);
      } catch (error) {
        logError('Failed to fetch license status for all patients', error);
      }
    },
    [],
  );

  useFocusEffect(
    useCallback(() => {
      fetchPatients(null, null);

      return () => {
        setShowFilter(false);
        resetFilter();
      };
    }, [fetchPatients, resetFilter]),
  );

  useEffect(() => {
    // Guard the fetch on focus without re-triggering the effect when focus flips.
    if (!isFocusedRef.current) {
      return;
    }
    fetchLicenseStatus(patients);
  }, [patients, fetchLicenseStatus]);

  useEffect(() => {
    if (!patients) {
      setShowLoadingSpinner(true);
    } else {
      setShowLoadingSpinner(false);
    }
  }, [patients]);

  return (
    <BodyWrapper scrollEnabled={false}>
      <View style={styles.sideBar}>
        {patients?.length === 0 && (
          <NoPatient handleAddPatient={handleAddPatient} />
        )}
        {patients && patients.length > 0 ? (
          <>
            <View style={styles.buttonWrapper}>
              <Button
                icon="filter"
                label={`Filter ${countFilterApplied > 0 ? `(${countFilterApplied})` : ''}`}
                look="tertiary"
                onPress={() => setShowFilter(!showFilter)}
                onHoverIn={handleHoverIn}
                onHoverOut={handleHoverOut}
                style={[
                  styles.filterButton,
                  isHovered && {
                    opacity: 0.7,
                    backgroundColor: colors.newUI.therapist.blue20,
                  },
                ]}
              />
              <Button
                testID="patient-list-add-button"
                look="primary"
                icon={'addUser'}
                onPress={handleAddPatient}
                label="Patient:in hinzufügen "
              />
            </View>
            {hasNoFilterResults ? (
              <Card>
                <View style={styles.noFilteredPatients}>
                  <Text style={styles.noFilteredPatientsTitle}>
                    Keine passenden Patient:innen gefunden
                  </Text>
                  <Text style={styles.noFilteredPatientsSubtitle}>
                    Ändern Sie die Filter, um weitere Patient:innen anzuzeigen.
                  </Text>
                </View>
              </Card>
            ) : (
              <Card styleBody={{ padding: 0 }}>
                <View style={{ maxHeight: listHeight }}>
                  <FlatList
                    scrollEnabled={true}
                    data={filteredPatients}
                    keyExtractor={(item) => item._id}
                    renderItem={({ item }) => {
                      const name = item.firstName + ' ' + item.lastName;
                      const idStyles = StyleSheet.create({
                        backgroundColor: { backgroundColor: colors.white },
                        color: { color: appTheme.textColor },
                      });
                      return (
                        <PressableNeolexon
                          testID={`patient-list-item-${item._id}`}
                          onPress={async () => {
                            try {
                              await changeID(item._id, true, patients);
                              changeSelectedPatient(item);
                              navigate('PatientProfile', {
                                id: item._id,
                                patients,
                                replacedTitle: replacedPatientProfileHeader({
                                  patients: undefined,
                                  patientId: item._id,
                                  patient: item,
                                }),
                              });
                            } catch (error) {
                              logError(
                                'Change selected patient in patient list',
                                {
                                  error,
                                  changeIdType: `changeId is typeof ${typeof changeID}`,
                                  changeSelectedPatientType: `changeSelectedPatient is typeof ${typeof changeSelectedPatient}`,
                                  patientsExist: patients?.length,
                                  patientExists:
                                    item.hasOwnProperty('_id') ||
                                    item.hasOwnProperty('id'),
                                },
                              );
                            }
                          }}
                          style={idStyles.backgroundColor}
                        >
                          <View style={styles.userBox}>
                            <View
                              style={[
                                styles.profileColor,
                                {
                                  backgroundColor:
                                    item.profileColor || DEFAULT_PROFILE_COLOR,
                                },
                              ]}
                            >
                              <Text
                                style={styles.profileInitials}
                                androidTrailingSpace={false}
                              >
                                {getInitials(
                                  item.firstName + ' ' + item.lastName,
                                )}
                              </Text>
                            </View>
                            <View style={{ flex: 1, minWidth: 0, gap: 4 }}>
                              <Text
                                numberOfLines={1}
                                ellipsizeMode="tail"
                                style={[idStyles.color]}
                              >
                                {name}
                              </Text>
                              {item.license &&
                              showWarningCheck(item.license) ? (
                                <View
                                  style={{
                                    flexDirection: 'row',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Icon
                                    style={{ marginRight: 5 }}
                                    name="warningOutline"
                                    size={16}
                                    color={colors.newUI.therapist.orange60}
                                  />
                                  <Text style={{ fontSize: 14 }}>
                                    Ablauf Patienten-App
                                  </Text>
                                </View>
                              ) : null}
                              {item.therapistTags?.length ? (
                                <View style={styles.tagsRow}>
                                  {item.therapistTags.map((tag) => (
                                    <Tag
                                      key={tag._id}
                                      label={tag.name}
                                      style={styles.therapistTag}
                                      labelStyle={styles.patientTagLabel}
                                    />
                                  ))}
                                </View>
                              ) : null}
                            </View>
                            <Icon
                              name="chevronRight"
                              size={20}
                              color={colors.newUI.therapist.primaryBlue80}
                              style={{ marginLeft: 'auto' }}
                            />
                          </View>
                          <Divider color={colors.lightGrey} width={2} />
                        </PressableNeolexon>
                      );
                    }}
                  />
                </View>
              </Card>
            )}
            <PatientFilter
              {...filterProps}
              isOpen={showFilter}
              onClose={() => {
                cancelFilter();
                setShowFilter(false);
              }}
              applyFilter={() => {
                filterProps.applyFilter();
                setShowFilter(false);
              }}
              resetFilter={() => {
                filterProps.resetFilter();
                setShowFilter(false);
              }}
            />
          </>
        ) : null}
        <Modal
          title="In der Demo nicht möglich"
          isVisible={isDemoTherapistVisible}
          confirmActionButton={
            <Button
              label="OK"
              look="primary"
              onPress={() => setIsDemoTherapistVisible(false)}
            />
          }
          onClose={() => setIsDemoTherapistVisible(false)}
        >
          <Text style={{ fontSize: 18 }}>
            Registrieren Sie sich bei neolexon, um Ihre eigenen Patient:innen
            und Übungssets zu erstellen.
          </Text>
        </Modal>
      </View>
    </BodyWrapper>
  );
}
